import React, { createContext, FC, memo, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { GridRowId } from '@mui/x-data-grid-pro';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { toast } from 'react-toastify';

import { IAmountTimeCategoryDTO, IDepartmentDTO, IJobDTO, ITimeCategoryBaseDTO, ITimesheetDTO } from 'src/api/dtos';
import CommonTimesheetRepository from 'src/api/repositories/common/v1/timesheet.repository';
import TimesheetRepository from 'src/api/repositories/ess/v1/timesheet.repository';
import { IError } from 'src/api/types';
import { TimesheetGrid } from 'src/features/timesheets/TimesheetGrid/TimesheetGrid.types';
import {
  buildTimesheetGrid,
  buildTimesheetUpdate,
  isValidUUID,
} from 'src/features/timesheets/TimesheetGrid/TimesheetGrid.utils';
import { UnsavedChangesProvider, useTranslate } from 'src/shared/providers';
import { getErrorMessage } from 'src/shared/utils';

import { useTimesheetData } from './TimesheetDataProvider';
import { useTimesheetQuery } from './TimesheetQueryProvider';
import { useTimesheetSelection } from './TimesheetSelectionProvider';

type TimesheetEditProps = {
  editingTimesheet: TimesheetGrid | undefined;
  initialTimesheet: TimesheetGrid | undefined;
  loading: boolean;
  isEdited: boolean;
  onDeleteRow: (rowId: GridRowId) => void;
  onDepartmentChange: (rowId: GridRowId, department: IDepartmentDTO | null) => void;
  onJobChange: (rowId: GridRowId, job: IJobDTO | undefined) => void;
  onAbsenceAndOverrideChange: (rowId: GridRowId, absenceAndOverride: ITimeCategoryBaseDTO | null) => void;
  onDurationChange: (rowId: GridRowId, date: string, duration: number) => void;
  onExpensesChange: (rowId: GridRowId, date: string, expenses: IAmountTimeCategoryDTO[]) => void;
  onNoteChange: (rowId: GridRowId, date: string, note: string | null) => void;
  onCopyLastWeek: () => void;
  onDeleteFile: (referenceId: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onSubmit: () => void;
};

const TimesheetEditContext = createContext<TimesheetEditProps>({} as TimesheetEditProps);

export const useTimesheetEdit = (): TimesheetEditProps => useContext<TimesheetEditProps>(TimesheetEditContext);

export const TimesheetEditProvider: FC<{ children: ReactNode }> = memo(({ children }) => {
  const { t } = useTranslate();

  const { selectedPayPeriodWeek } = useTimesheetQuery();
  const { timesheet, loadingTimesheet, isInvalid } = useTimesheetData();
  const { onClear } = useTimesheetSelection();

  const [editingTimesheet, setEditingTimesheet] = useState<TimesheetGrid>();
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
  const [initialTimesheet, setInitialTimesheet] = useState<TimesheetGrid>();

  const [updateTimesheet, { isLoading: isUpdatingTimesheet }] =
    TimesheetRepository.endpoints.useUpdateTimesheetGridMutation();

  const [submitTimesheet, { isLoading: isSubmittingTimesheet }] =
    TimesheetRepository.endpoints.useSubmitTimesheetMutation();

  const [getCopyLastWeek, { isLoading: isLoadingCopyLastWeek }] =
    TimesheetRepository.endpoints.useLazyGetCopyLastWeekQuery();

  const loading = loadingTimesheet || isUpdatingTimesheet || isLoadingCopyLastWeek || isSubmittingTimesheet;

  const isEdited = useMemo(
    () => !!initialTimesheet && !!editingTimesheet && !isEqual(initialTimesheet, editingTimesheet),
    [initialTimesheet, editingTimesheet],
  );

  const initializeTimesheet = (timesheet: ITimesheetDTO | undefined) => {
    setEditingTimesheet(timesheet ? buildTimesheetGrid(timesheet) : undefined);
    setInitialTimesheet(timesheet ? buildTimesheetGrid(timesheet) : undefined);
    onClear();
  };

  const handleDeleteRow = (rowId: GridRowId) => {
    setEditingTimesheet((timesheet) => {
      if (!timesheet) {
        return;
      }

      const clonedTimesheet = cloneDeep(timesheet);

      return {
        ...clonedTimesheet,
        rows: clonedTimesheet.rows.filter((row) => row.id !== rowId),
      };
    });
  };

  const handleDepartmentChange = (rowId: GridRowId, department: IDepartmentDTO | null) => {
    setEditingTimesheet((timesheet) => {
      if (!timesheet) {
        return;
      }

      const clonedTimesheet = cloneDeep(timesheet);

      return {
        ...clonedTimesheet,
        rows: clonedTimesheet.rows.map((row) => (rowId === row.id ? { ...row, department } : row)),
      };
    });
  };

  const handleJobChange = (rowId: GridRowId, job: IJobDTO | undefined) => {
    setEditingTimesheet((timesheet) => {
      if (!timesheet) {
        return;
      }

      const clonedTimesheet = cloneDeep(timesheet);

      return {
        ...clonedTimesheet,
        rows: clonedTimesheet.rows.map((row) => {
          if (rowId !== row.id) {
            return row;
          }

          if (!job?.level) {
            return row;
          }

          const clonedRow = cloneDeep(row);

          clonedRow.jobs[job.level - 1] = job;

          return clonedRow;
        }),
      };
    });
  };

  const handleAbsenceAndOverrideChange = (rowId: GridRowId, absenceAndOverride: ITimeCategoryBaseDTO | null) => {
    setEditingTimesheet((timesheet) => {
      if (!timesheet) {
        return;
      }

      const clonedTimesheet = cloneDeep(timesheet);

      return {
        ...clonedTimesheet,
        rows: clonedTimesheet.rows.map((row) => (rowId === row.id ? { ...row, absenceAndOverride } : row)),
      };
    });
  };

  const handleDurationChange = (rowId: GridRowId, date: string, duration: number) => {
    setEditingTimesheet((timesheet) => {
      if (!timesheet) {
        return;
      }

      const clonedTimesheet = cloneDeep(timesheet);

      return {
        ...clonedTimesheet,
        rows: clonedTimesheet.rows.map((row) =>
          rowId === row.id
            ? { ...row, cells: row.cells.map((cell) => (date === cell.date ? { ...cell, duration } : cell)) }
            : row,
        ),
      };
    });
  };

  const handleExpensesChange = (rowId: GridRowId, date: string, expenses: IAmountTimeCategoryDTO[]) => {
    setEditingTimesheet((timesheet) => {
      if (!timesheet) {
        return;
      }

      const clonedTimesheet = cloneDeep(timesheet);

      return {
        ...clonedTimesheet,
        rows: clonedTimesheet.rows.map((row) =>
          rowId === row.id
            ? { ...row, cells: row.cells.map((cell) => (date === cell.date ? { ...cell, expenses } : cell)) }
            : row,
        ),
      };
    });
  };

  const handleNoteChange = (rowId: GridRowId, date: string, note: string | null) => {
    setEditingTimesheet((timesheet) => {
      if (!timesheet) {
        return;
      }

      const clonedTimesheet = cloneDeep(timesheet);

      return {
        ...clonedTimesheet,
        rows: clonedTimesheet.rows.map((row) =>
          rowId === row.id
            ? { ...row, cells: row.cells.map((cell) => (date === cell.date ? { ...cell, note } : cell)) }
            : row,
        ),
      };
    });
  };

  const handleCopyLastWeek = async () => {
    if (!selectedPayPeriodWeek || loading) {
      return;
    }

    const { startDate, endDate } = selectedPayPeriodWeek;

    try {
      const copyLastWeek = await getCopyLastWeek({ startDate, endDate }).unwrap();

      setEditingTimesheet(buildTimesheetGrid(copyLastWeek));
      onClear();

      toast.info(t('timesheets.youHaveSuccessfullyCopiedLastWeek'), {
        type: toast.TYPE.SUCCESS,
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    }
  };

  const handleDeleteFile = (referenceId: string) => {
    setFilesToDelete((files) => [...files, referenceId]);
  };

  const uploadTimesheetAttachments = async (timesheet: TimesheetGrid) => {
    for (const row of timesheet.rows) {
      for (const cell of row.cells) {
        for (const expense of cell.expenses) {
          if (expense.attachment?.file && !isValidUUID(expense.attachment.referenceId)) {
            const formData = new FormData();
            formData.append('attachment', expense.attachment.file);

            const { referenceId: newReferenceId } = await CommonTimesheetRepository.uploadAttachment(formData);
            expense.attachment.referenceId = newReferenceId;
          }
        }
      }
    }
  };

  const deleteTimesheetAttachments = async () => {
    await Promise.all(
      filesToDelete.map(async (referenceId) => {
        if (isValidUUID(referenceId)) {
          await CommonTimesheetRepository.deleteAttachment({
            referenceId,
          });
        }
      }),
    );
  };

  const handleSave = async () => {
    if (!selectedPayPeriodWeek || !editingTimesheet || !isEdited || loading) {
      return;
    }

    const { startDate, endDate } = selectedPayPeriodWeek;

    try {
      // Deep copy to avoid mutating state
      const updatedTimesheet = cloneDeep(editingTimesheet);
      // Upload any new file attachments and update referenceIds
      await uploadTimesheetAttachments(updatedTimesheet);
      setEditingTimesheet(updatedTimesheet);

      // Delete any removed file attachments
      await deleteTimesheetAttachments();

      const payload = buildTimesheetUpdate(startDate, endDate, updatedTimesheet);

      await updateTimesheet(payload).unwrap();

      toast.info(t('timesheets.youHaveSuccessfullySavedTheTimesheet'), {
        type: toast.TYPE.SUCCESS,
      });
      return true;
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
      return false;
    }
  };

  const handleCancel = () => {
    if (!timesheet || !isEdited || loading) {
      return;
    }

    initializeTimesheet(timesheet);
  };

  const handleSubmit = async () => {
    if (!selectedPayPeriodWeek || loading) {
      return;
    }

    const { startDate, endDate } = selectedPayPeriodWeek;
    try {
      await submitTimesheet({ startDate, endDate }).unwrap();

      toast.info(t('timesheets.youHaveSuccessfullySubmittedTheTimesheet'), {
        type: toast.TYPE.SUCCESS,
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    }
  };

  // Reset initial and editing inputs on timesheet change
  useEffect(() => {
    if (loadingTimesheet) {
      return;
    }

    if (!timesheet || isInvalid) {
      initializeTimesheet(undefined);
    } else {
      initializeTimesheet(timesheet);
    }
  }, [timesheet, loadingTimesheet, isInvalid]);

  return (
    <UnsavedChangesProvider onSave={handleSave} isEdited={isEdited} loading={loading}>
      <TimesheetEditContext.Provider
        value={{
          editingTimesheet,
          initialTimesheet,
          loading,
          isEdited,
          onDeleteRow: handleDeleteRow,
          onDepartmentChange: handleDepartmentChange,
          onJobChange: handleJobChange,
          onAbsenceAndOverrideChange: handleAbsenceAndOverrideChange,
          onDurationChange: handleDurationChange,
          onExpensesChange: handleExpensesChange,
          onNoteChange: handleNoteChange,
          onCopyLastWeek: handleCopyLastWeek,
          onDeleteFile: handleDeleteFile,
          onSave: handleSave,
          onCancel: handleCancel,
          onSubmit: handleSubmit,
        }}
      >
        {children}
      </TimesheetEditContext.Provider>
    </UnsavedChangesProvider>
  );
});
